import moment from "moment";

import type { SteppedTimeRange } from "@/lib/api";

export const DEFAULT_OBSERVATION_STEP = "PT10M";

const TEN_MIN_MS = 10 * 60 * 1000;

/** Floors to UTC 10-minute marks aligned on the hour (:00, :10, … :50). */
export function floorToUtcTenMinuteAligned(date: Date): Date {
  const d = new Date(date);
  d.setUTCSeconds(0, 0);
  const minutes = d.getUTCMinutes();
  d.setUTCMinutes(minutes - (minutes % 10));
  return d;
}

export function toSteppedTenMinuteRange(timeRange: {
  timeStart: Date;
  timeEnd: Date;
}): SteppedTimeRange {
  return {
    begin: floorToUtcTenMinuteAligned(timeRange.timeStart).toISOString(),
    end: floorToUtcTenMinuteAligned(timeRange.timeEnd).toISOString(),
    step: DEFAULT_OBSERVATION_STEP,
  };
}

export function buildTenMinuteAlignedTimestamps(timeRange: {
  timeStart: Date;
  timeEnd: Date;
}): string[] {
  const start = floorToUtcTenMinuteAligned(timeRange.timeStart).getTime();
  const end = floorToUtcTenMinuteAligned(timeRange.timeEnd).getTime();
  const timestamps: string[] = [];
  for (let t = start; t <= end; t += TEN_MIN_MS) {
    timestamps.push(new Date(t).toISOString());
  }
  return timestamps;
}

export function millisecondsToIsoDuration(ms: number): string {
  return moment.duration(ms).toISOString();
}

export function steppedTimeRangesEqual(a: SteppedTimeRange, b: SteppedTimeRange): boolean {
  return a.begin === b.begin && a.end === b.end && a.step === b.step;
}

/**
 * Derives a regular stepped range from observation timestamps: min/max bounds and
 * the smallest positive gap between consecutive sorted unique times.
 */
export function deriveSteppedTimeRangeFromDatapoints(
  timestamps: string[],
): SteppedTimeRange | null {
  const unique = [...new Set(timestamps)].sort((a, b) => +new Date(a) - +new Date(b));
  if (unique.length === 0) return null;

  if (unique.length === 1) {
    const instant = new Date(unique[0]);
    return {
      begin: instant.toISOString(),
      end: instant.toISOString(),
      step: DEFAULT_OBSERVATION_STEP,
    };
  }

  const msValues = unique.map((timestamp) => +new Date(timestamp));
  let minStepMs = Infinity;
  for (let i = 1; i < msValues.length; i++) {
    const delta = msValues[i] - msValues[i - 1];
    if (delta > 0 && delta < minStepMs) {
      minStepMs = delta;
    }
  }

  const step =
    minStepMs === Infinity
      ? DEFAULT_OBSERVATION_STEP
      : millisecondsToIsoDuration(minStepMs);

  return {
    begin: new Date(msValues[0]).toISOString(),
    end: new Date(msValues[msValues.length - 1]).toISOString(),
    step,
  };
}

type TimelineDatapoint = { timestamp: string };

/** Matches player timestep to a series datapoint (exact instant). */
export function datapointAtTimelineTime<T extends TimelineDatapoint>(
  datapoints: T[],
  currentTime: Date | undefined,
): T | undefined {
  if (!currentTime) return undefined;

  const currentMs = +currentTime;
  return datapoints.find((point) => +new Date(point.timestamp) === currentMs);
}
